/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createEffect, createSignal, onCleanup } from 'davaux/client'
import {
  fontSize,
  radius,
  resolveVariantColors,
  type UiColor,
  type UiGradient,
  type UiRadius,
  type UiSize,
} from './types.js'

/** Visual style variant controlling background, border, and color treatment. */
export type ButtonVariant =
  | 'filled'
  | 'light'
  | 'outline'
  | 'subtle'
  | 'transparent'
  | 'white'
  | 'default'
  | 'gradient'

/** A single item in the Menu dropdown. */
export interface MenuItemDef {
  /** Display text. */
  label: string
  /** When provided, the item renders as `<a>`. */
  href?: string
  /** When true, the item is dimmed and non-interactive. */
  disabled?: boolean
  /** When true, renders a horizontal divider in place of a label item. */
  divider?: boolean
  /** Optional theme color applied to the item label. */
  color?: string
}

function Menu({
  trigger = 'Menu',
  items = [],
  position = 'bottom-start',
  disabled = false,
  variant = 'default',
  color,
  size,
  radius: r,
  gradient,
  fullWidth,
}: {
  trigger?: string
  items?: MenuItemDef[]
  position?: string
  disabled?: boolean
  /** Visual style variant applied to the trigger button. */
  variant?: ButtonVariant
  /** Base color used to derive trigger background, hover, and border. */
  color?: UiColor
  /** Trigger button size; prefix with `compact-` for reduced padding variants. */
  size?: UiSize | `compact-${UiSize}`
  /** Border-radius of the trigger button corners. */
  radius?: UiRadius
  /** Gradient definition used when `variant` is `'gradient'`. */
  gradient?: UiGradient
  /** Stretches the trigger button to fill its container width. */
  fullWidth?: boolean
}) {
  const [open, setOpen] = createSignal(false)
  const rootRef = { current: null as Element | null }

  function toggle() {
    if (!disabled) setOpen(!open())
  }

  function close() {
    setOpen(false)
  }

  createEffect(() => {
    if (!open()) return
    function handleOutsideClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close()
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('click', handleOutsideClick)
    document.addEventListener('keydown', handleKeyDown)
    onCleanup(() => {
      document.removeEventListener('click', handleOutsideClick)
      document.removeEventListener('keydown', handleKeyDown)
    })
  })

  function handleItemClick(item: MenuItemDef) {
    if (!item.disabled && !item.divider) close()
  }

  // Build trigger CSS variables — mirrors Button.ts logic
  const cssVars: Record<string, string> = {}

  const baseSize = size?.startsWith('compact-') ? size.replace('compact-', '') : size
  if (baseSize !== undefined && size !== undefined) {
    cssVars['--dv-button-height'] = `var(--dv-button-height-${size})`
    cssVars['--dv-button-padding-x'] = `var(--dv-button-padding-x-${size})`
    cssVars['--dv-button-fz'] = fontSize(baseSize as UiSize) ?? ''
  }

  if (r !== undefined) cssVars['--dv-button-radius'] = radius(r) ?? ''

  if (variant === 'gradient' && gradient) {
    const deg = gradient.deg ?? 45
    cssVars['--dv-button-bg'] = `linear-gradient(${deg}deg, ${gradient.from}, ${gradient.to})`
    cssVars['--dv-button-hover'] = `linear-gradient(${deg}deg, ${gradient.from}, ${gradient.to})`
    cssVars['--dv-button-color'] = 'var(--dv-color-white)'
    cssVars['--dv-button-bd'] = 'none'
  } else if (color !== undefined || variant !== 'default') {
    const colors = resolveVariantColors(color, variant)
    cssVars['--dv-button-bg'] = colors.background
    cssVars['--dv-button-hover'] = colors.hover
    cssVars['--dv-button-color'] = colors.color
    cssVars['--dv-button-bd'] = colors.border
  }

  const triggerStyle = Object.keys(cssVars).length > 0 ? cssVars : undefined

  return (
    <div ref={rootRef} class='dv-menu' data-opened={() => open() || undefined}>
      <button
        type='button'
        class='dv-menu-target'
        style={triggerStyle}
        data-variant={variant}
        data-size={size || undefined}
        data-full-width={fullWidth || undefined}
        onClick={() => toggle()}
        aria-haspopup='menu'
        aria-expanded={() => open()}
        disabled={disabled}
      >
        <span class='dv-button-inner'>
          <span class='dv-button-label'>{trigger}</span>
        </span>
      </button>
      <div
        class='dv-menu-dropdown'
        role='menu'
        data-position={position}
        data-opened={() => open() || undefined}
      >
        {items.map((item) =>
          item.divider ? (
            <div class='dv-menu-divider' role='separator' />
          ) : item.href ? (
            <a
              class='dv-menu-item'
              href={item.disabled ? undefined : item.href}
              role='menuitem'
              data-disabled={item.disabled || undefined}
              data-color={item.color}
              onClick={() => handleItemClick(item)}
            >
              {item.label}
            </a>
          ) : (
            <button
              type='button'
              class='dv-menu-item'
              role='menuitem'
              disabled={item.disabled}
              data-color={item.color}
              onClick={() => handleItemClick(item)}
            >
              {item.label}
            </button>
          ),
        )}
      </div>
    </div>
  )
}

export default reactive(Menu)
